import * as React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import ListItem from 'LuxKit/List/ListItem'
import NestableClickable from 'components/Common/NestableClickable'
import OptionsButton from 'tripPlanner/components/Documents/OptionsButton'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import { formatChatLabel, formatChatSubtitle } from './ChatTripPlannerPage/utils/format'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'

const ChatListItem = styled(ListItem)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  &:last-child {
    border-bottom: none;
  }

  ${mediaHoverable} {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }
`

const MenuActions = styled.div`
  display: flex;
  flex-direction: column;
`

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: ${rem(8)} ${rem(16)};
  text-align: left;
  cursor: pointer;
  font-size: ${rem(14)};
  color: ${props => props.theme.palette.neutral.default.one};

  &:hover {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

interface Props {
  chat: TripPlannerChat
  onChatClick: (chatId: number) => () => void
  onDeleteClick: (chatId: number, closeMenu: () => void) => (event: React.MouseEvent) => void
}

function DrawerChatListItem({ chat, onChatClick, onDeleteClick }: Props) {
  const intl = useIntl()
  const lastActivityTimestamp = getChatLastActivityTimestamp(chat)
  const chatTitle = chat.name?.trim() || formatChatLabel(lastActivityTimestamp)
  const subtitle = formatChatSubtitle(lastActivityTimestamp, intl)

  const chatActionsTitle = intl.formatMessage({
    id: '-bD6A4Qe-0a_1LR2lLWn',
    defaultMessage: 'Chat actions',
    description: 'Accessible title for the chat actions dropdown menu in the drawer list item',
  })

  const deleteText = intl.formatMessage({
    id: 'NYvcrggtkoIHuLPifYLV',
    defaultMessage: 'Delete',
    description: 'Button label to delete a chat conversation from the TripPlanner drawer list',
  })

  return (
    <ChatListItem
      forwardedAs={NestableClickable}
      onClick={onChatClick(chat.id)}
      title={chatTitle}
      titleWeight="bold"
      titleLineClamp={1}
      subtitle={subtitle}
      size="medium"
    >
      <OptionsButton
        title={chatActionsTitle}
        bodyContent={(closeMenu) => (
          <MenuActions>
            <MenuButton
              type="button"
              onClick={onDeleteClick(chat.id, closeMenu)}
            >
              {deleteText}
            </MenuButton>
          </MenuActions>
        )}
      />
    </ChatListItem>
  )
}

export default DrawerChatListItem
